import { CommonModule } from '@angular/common';
import { Component, inject } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { finalize, take } from 'rxjs';
import { BlogPayload, ServicesService } from 'src/services/services.service';

export interface Blog {
  blog_id?: string | number;
  title: string;
  short_description: string;
  description: string;
  author: string;
  status: string;
  is_active: string;
  meta_title: string | null;
  meta_keywords: string | null;
  meta_description: string | null;
  h1_tag: string | null;
  image_path: string | null;
  blog_image?: string;
  blog_image_existing?: string;
  remove_existing_image?: string;
}

export interface BlogFormDialogData {
  mode: 'add' | 'edit';
  blog?: Blog;
}

@Component({
  selector: 'app-blog-form-dialog',
  templateUrl: './blog-form-dialog.component.html',
  styleUrls: ['./blog-form-dialog.component.scss']
})
export class BlogFormDialogComponent {
  quillInstance: any;

  quillConfig = {
    toolbar: [
      [{ header: [1, 2, 3, 4, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ list: 'ordered' }, { list: 'bullet' }],
      [{ color: [] }, { background: [] }],
      [{ align: [] }],
      ['link', 'blockquote', 'code-block'],
      ['clean']
    ],
    clipboard: {
      matchVisual: false
    }
  };

  private readonly fb = inject(FormBuilder);
  private readonly blogService = inject(ServicesService);
  private readonly dialogRef = inject(MatDialogRef<BlogFormDialogComponent>);
  readonly data = inject<BlogFormDialogData>(MAT_DIALOG_DATA);

  readonly isEditMode = this.data.mode === 'edit';

  imagefileURL: string | ArrayBuffer | null = null;
  imagePath: string = ''; // Holds uploaded filename (e.g., "abc.webp")
  existingImage: string = ''; // Holds ONLY existing filename (e.g., "abc.webp")
  imgType: string = 'blog';

  imagePreview: string | null = null;
  uploadingImage = false;
  saving = false;
  errorMessage = '';

  readonly form = this.fb.nonNullable.group({
    title: ['', [Validators.required, Validators.maxLength(255)]],
    short_description: ['', Validators.required],
    description: ['', Validators.required],
    author: ['', Validators.required],
    status: ['Draft', Validators.required],
    is_active: ['1', Validators.required],
    meta_title: [''],
    meta_keywords: [''],
    meta_description: [''],
    h1_tag: ['']
  });

  constructor() {
    const blog = this.data.blog;

    if (this.isEditMode && blog) {
      this.form.patchValue({
        title: blog.title ?? '',
        short_description: blog.short_description ?? '',
        description: blog.description ?? '',
        author: blog.author ?? '',
        status: blog.status ?? 'Draft',
        is_active: blog.is_active ?? '1',
        meta_title: blog.meta_title ?? '',
        meta_keywords: blog.meta_keywords ?? '',
        meta_description: blog.meta_description ?? '',
        h1_tag: blog.h1_tag ?? ''
      });

      // Extract raw image string
      const rawImage = blog.image_path || blog.blog_image || blog.blog_image_existing || '';
      if (rawImage) {
        // Set imagePreview as full URL or raw value for preview UI
        this.imagePreview = rawImage;
        // Clean existingImage to store ONLY the filename
        this.existingImage = this.extractFilename(rawImage);
      }
    }
  }

  /**
   * Helper to extract only the filename from a URL or full path
   */
  private extractFilename(urlOrPath: string): string {
    if (!urlOrPath) return '';
    const cleanPath = urlOrPath.split('?')[0]; // strip query string if any
    return cleanPath.substring(cleanPath.lastIndexOf('/') + 1);
  }

  onEditorCreated(quill: any) {
    this.quillInstance = quill;

    quill.scroll.getNativeRange = function () {
      const selection = window.getSelection();
      if (!selection || selection.rangeCount === 0) {
        return null;
      }
      return selection.getRangeAt(0);
    };

    const originalSelectionChange = quill.selection.update;
    quill.selection.update = function (source: string) {
      if (source === 'user') {
        const dialogContent = document.querySelector('.form-content');
        const scrollTop = dialogContent ? dialogContent.scrollTop : 0;

        originalSelectionChange.apply(quill.selection, arguments);

        if (dialogContent && dialogContent.scrollTop !== scrollTop) {
          dialogContent.scrollTop = scrollTop;
        }
      } else {
        originalSelectionChange.apply(quill.selection, arguments);
      }
    };
  }

  pasteRawHtmlFromAi(rawHtml: string) {
    if (this.quillInstance) {
      this.quillInstance.clipboard.dangerouslyPasteHTML(0, rawHtml);
    }
  }

  get title() {
    return this.form.controls.title;
  }

  get shortDescription() {
    return this.form.controls.short_description;
  }

  get description() {
    return this.form.controls.description;
  }

  previewpImage(files: any) {
    if (!files || files.length === 0 || this.uploadingImage) {
      return;
    }
    const file = files[0];
    const mimeType = file.type;
    if (!mimeType.match(/image\/*/)) {
      this.errorMessage = 'Only image files are supported.';
      return;
    }

    this.errorMessage = '';
    this.uploadingImage = true;

    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      this.imagefileURL = reader.result;
      this.imagePreview = reader.result as string;
      this.imgType = 'blog';
      const fileName = file.name;

      this.blogService
        .uploadBlogImage(this.imagefileURL, this.imgType, fileName)
        .pipe(take(1))
        .subscribe({
          next: (data: any) => {
            // Store strictly the filename returned by backend (e.g. "abc.webp")
            this.imagePath = this.extractFilename(data['image_name'] || '');
            this.uploadingImage = false;
          },
          error: (err: any) => {
            console.error('Image upload failed', err);
            this.errorMessage = 'Failed to upload image. Please try again.';
            this.uploadingImage = false;
          }
        });
    };
  }

  removeSelectedImage(): void {
    this.imagefileURL = null;
    this.imagePath = '';
    this.existingImage = '';
    this.imagePreview = null;
  }

  saveBlog(): void {
    if (this.form.invalid || this.saving || this.uploadingImage) {
      this.form.markAllAsTouched();
      return;
    }

    if (this.isEditMode && !this.data.blog?.blog_id) {
      this.errorMessage = 'Blog ID is missing. Unable to update this blog.';
      return;
    }

    this.saving = true;
    this.errorMessage = '';

    const values = this.form.getRawValue();

    // Pass only clean image filenames to backend
    const payload: BlogPayload = {
      ...values,
      ...(this.isEditMode ? { blog_id: this.data.blog!.blog_id } : {}),

      blog_image: this.imagePath ? this.imagePath : (this.existingImage ? this.existingImage : null),
      blog_image_existing: this.imagePath ? '' : this.existingImage,
      remove_existing_image: (this.isEditMode && !this.imagePath && !this.existingImage) ? '1' : '0'
    };

    const request$ = this.isEditMode
      ? this.blogService.updateBlog(String(this.data.blog!.blog_id), payload)
      : this.blogService.createBlog(payload);

    request$
      .pipe(
        take(1),
        finalize(() => (this.saving = false))
      )
      .subscribe({
        next: (response: unknown) => {
          if (!this.isSuccessfulResponse(response)) {
            this.errorMessage = this.getResponseMessage(response) || 'Unable to save the blog.';
            return;
          }

          this.dialogRef.close({
            saved: true,
            mode: this.data.mode
          });
        },
        error: (error: { error?: unknown }) => {
          console.error('Failed to save blog:', error);
          this.errorMessage =
            this.getResponseMessage(error.error) || 'Unable to save the blog. Please try again.';
        }
      });
  }

  private isSuccessfulResponse(response: unknown): boolean {
    if (response === null || typeof response !== 'object') return false;

    const result = response as { flag?: boolean | number | string; success?: boolean; status?: string };
    return (
      result.flag === true ||
      result.flag === 1 ||
      result.flag === '1' ||
      result.success === true ||
      result.status?.toLowerCase() === 'success'
    );
  }

  private getResponseMessage(response: unknown): string {
    if (!response || typeof response !== 'object') return '';
    const result = response as { msg?: string; message?: string };
    return result.msg ?? result.message ?? '';
  }

  cancel(): void {
    this.dialogRef.close();
  }
}